// CppListPage — list of Construction Phase Plan / Pre-Construction Information
// records. Used as a global page (/app/cpp) or embedded compact panel scoped
// to one project (via ProjectCppPanel). Mirrors RamsDashboardPage's layout.
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Plus, Loader2, Download, Trash2, FileText } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, filenameFromResponse } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Pill from "@/components/Pill";
import CppNewDialog from "@/components/cpp/CppNewDialog";

const CAN_EDIT_ROLES = new Set(["director", "administrator", "consultant"]);
const DOC_LABEL = { cpp: "Construction Phase Plan", pci: "Pre-Construction Information" };
const STATUS_TONE = { Draft: "neutral", "In progress": "amber", "Ready for review": "blue", Approved: "green", Issued: "green" };

export default function CppListPage({ presetClientId, presetProjectId, compact }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchParams, setSearchParams] = useSearchParams();
    const [newOpen, setNewOpen] = useState(!!searchParams.get("new"));
    const initialDocType = searchParams.get("new") === "pci" ? "pci" : "cpp";
    const { user } = useAuth();
    const navigate = useNavigate();
    const canEdit = user && CAN_EDIT_ROLES.has(user.role);
    const params = { ...(presetClientId ? { client_id: presetClientId } : {}), ...(presetProjectId ? { project_id: presetProjectId } : {}) };

    const closeNewDialog = () => {
        setNewOpen(false);
        if (searchParams.get("new")) setSearchParams({});
    };

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/cpp", { params });
            setRows(data);
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not load records"); }
        setLoading(false);
    }, [presetClientId, presetProjectId]);

    useEffect(() => { refresh(); }, [refresh]);

    const del = async (id) => {
        if (!window.confirm("Delete this record?")) return;
        try {
            await api.delete(`/cpp/${id}`);
            toast.success("Deleted");
            refresh();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not delete"); }
    };

    const exportPdf = async (r) => {
        try {
            const res = await api.get(`/cpp/${r.id}/export/pdf`, { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url; a.download = filenameFromResponse(res, `${r.title || "document"}.pdf`);
            a.click();
            URL.revokeObjectURL(url);
        } catch (e) { toast.error("PDF export failed"); }
    };

    return (
        <div data-testid="cpp-list-page" className="space-y-5">
            {!compact && (
                <div className="flex items-end justify-between flex-wrap gap-3">
                    <div>
                        <span className="inline-block pb-1 text-[10.5px] font-bold tracking-[.08em] uppercase"
                            style={{ borderBottom: "2px solid var(--brand-accent, #C75A1B)", color: "#6F6A64" }}>Construction Docs</span>
                        <h2 className="text-2xl font-bold tracking-tight mt-2 text-[#1F1F1F]">CPP / PCI Plans</h2>
                    </div>
                    {canEdit && (
                        <button data-testid="cpp-new-btn" onClick={() => setNewOpen(true)}
                            className="inline-flex items-center gap-2 text-sm font-semibold px-3.5 py-2 rounded-lg text-white"
                            style={{ background: "var(--brand-accent, #C75A1B)" }}>
                            <Plus size={15} /> New CPP / PCI
                        </button>
                    )}
                </div>
            )}
            {compact && canEdit && (
                <div className="flex justify-end">
                    <button data-testid="cpp-new-btn-compact" onClick={() => setNewOpen(true)}
                        className="inline-flex items-center gap-2 text-sm font-semibold px-3.5 py-2 rounded-lg text-white"
                        style={{ background: "var(--brand-accent, #C75A1B)" }}>
                        <Plus size={15} /> New CPP / PCI
                    </button>
                </div>
            )}

            <div className="rounded-2xl bg-white border border-[#E5E0DA] overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-[#F5F3F0] text-left text-[11px] uppercase tracking-wider text-[#6F6A64] border-b border-[#E5E0DA]">
                        <tr>
                            <th className="px-4 py-3 font-semibold">Title</th>
                            <th className="px-4 py-3 font-semibold">Type</th>
                            {!presetProjectId && <th className="px-4 py-3 font-semibold">Project</th>}
                            <th className="px-4 py-3 font-semibold">Sections</th>
                            <th className="px-4 py-3 font-semibold">Status</th>
                            <th className="px-4 py-3 font-semibold">Updated</th>
                            <th className="px-4 py-3 font-semibold w-40"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={7} className="text-center py-10 text-[#9A968F]"><Loader2 size={16} className="inline animate-spin mr-2" />Loading…</td></tr>
                        ) : rows.length === 0 ? (
                            <tr><td colSpan={7} className="text-center py-10 text-[#9A968F]" data-testid="cpp-empty-state">No CPP / PCI records yet.</td></tr>
                        ) : rows.map((r) => (
                            <tr key={r.id} data-testid={`cpp-row-${r.id}`} className="border-b border-[#F0EDE8] hover:bg-[#F5F3F0]">
                                <td className="px-4 py-3 font-semibold text-[#1F1F1F] cursor-pointer" onClick={() => navigate(`/app/cpp/${r.id}`)}>
                                    <FileText size={13} className="inline mr-1.5 -mt-0.5" style={{ color: "var(--brand-accent,#C75A1B)" }} />
                                    {r.title || "Untitled"}
                                </td>
                                <td className="px-4 py-3"><Pill tone={r.doc_type === "cpp" ? "blue" : "purple"}>{DOC_LABEL[r.doc_type]}</Pill></td>
                                {!presetProjectId && <td className="px-4 py-3 text-[#6F6A64]">{r.project_name || "—"}</td>}
                                <td className="px-4 py-3 text-[#6F6A64]">{r.section_count}</td>
                                <td className="px-4 py-3"><Pill tone={STATUS_TONE[r.status] || "neutral"}>{r.status}</Pill></td>
                                <td className="px-4 py-3 text-[#6F6A64]">{r.updated_at ? new Date(r.updated_at).toLocaleDateString("en-GB") : "—"}</td>
                                <td className="px-4 py-3">
                                    <div className="flex items-center justify-end gap-2.5">
                                        <button data-testid={`cpp-export-${r.id}`} onClick={() => exportPdf(r)}
                                            className="inline-flex items-center gap-1 text-[12px] font-semibold text-[#6F6A64] hover:text-[#1F1F1F] hover:underline"><Download size={11} />PDF</button>
                                        {canEdit && (
                                            <button data-testid={`cpp-delete-${r.id}`} onClick={() => del(r.id)}
                                                className="inline-flex items-center gap-1 text-[12px] font-semibold text-[#9A968F] hover:text-red-600 hover:underline"><Trash2 size={11} />Delete</button>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {newOpen && (
                <CppNewDialog presetClientId={presetClientId} presetProjectId={presetProjectId} initialDocType={initialDocType}
                    onClose={closeNewDialog}
                    onCreated={(rec) => { closeNewDialog(); navigate(`/app/cpp/${rec.id}`); }} />
            )}
        </div>
    );
}
